import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { App as AntdApp, ConfigProvider, theme as antdTheme } from 'antd';

import { ModelCapabilityProvider } from '@/features/model-capability';
import { useCanvasAccessAllowed } from '@/hooks/useFeatureFlag';
import { UploadCenterProvider } from '@/services/upload';

import Layout from './layouts/Layout';
import { AuthProvider } from './providers/AuthProvider';
import { UserProvider } from './providers/UserProvider';
import LandingEntryRoute from './router/LandingEntryRoute';
import ProtectedRoute from './router/ProtectedRoute';

const CanvasRouteGuard: React.FC<React.ComponentProps<typeof CanvasPage>> = (props) => {
  const isCanvasRouteAllowed = useCanvasAccessAllowed();

  if (!isCanvasRouteAllowed) {
    return <Navigate to="/" replace />;
  }

  return <CanvasPage {...props} />;
};

const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center h-full w-full min-h-[200px]">
    <svg className="w-8 h-8 animate-spin text-theme-color" viewBox="0 0 24 24" fill="none">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="2.5"
      />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  </div>
);

const LandingPage = React.lazy(() => import('@/pages/LandingPage'));
const LoginPage = React.lazy(() => import('@/pages/LoginPage'));
const CanvasPage = React.lazy(() => import('@/pages/CanvasPage'));
const UserGuidePage = React.lazy(() => import('@/pages/UserGuide'));
const ProjectDashboard = React.lazy(() => import('@/pages/ProjectDashboard'));
const AgentAccessResolvePage = React.lazy(() => import('@/pages/AgentAccessResolvePage'));
const SkillManagement = React.lazy(() => import('@/pages/SkillManagement'));
const TeamPage = React.lazy(() => import('@/pages/TeamPage'));
const TeamKnowledgePage = React.lazy(() => import('@/pages/TeamKnowledgePage'));
const TeamWorkspacePage = React.lazy(() => import('@/pages/TeamWorkspacePage'));

const THEME_STORAGE_KEY = 'manclaw-theme';

const App: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');

  useEffect(() => {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    // 有明确存储记录时遵从用户选择，否则默认夜间模式
    if (storedTheme === 'light') {
      setTheme('light');
    } else {
      setTheme('dark');
    }
  }, []);

  const handleThemeToggle = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
  };

  const isDark = theme === 'dark';

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // AntD 主题 token，对齐项目日夜间颜色规范
  const antdConfigToken = {
    colorPrimary: isDark ? '#2ad99f' : '#23b383',
    colorBgBase: isDark ? '#0a0a0a' : '#ffffff',
    colorBgContainer: isDark ? '#181818' : '#ffffff',
    colorBgElevated: isDark ? '#181818' : '#ffffff',
    colorBorder: isDark ? '#393a3f' : '#f0f1f2',
    colorText: isDark ? '#fafbff' : '#060a26',
    colorTextSecondary: isDark ? '#9496a0' : '#646a8c',
    colorTextTertiary: isDark ? 'rgba(250,251,255,0.45)' : 'rgba(6,10,38,0.45)',
    colorSplit: isDark ? 'rgba(250,251,255,0.12)' : 'rgba(6,10,38,0.12)',
    borderRadius: 9,
    fontFamily: 'Inter, system-ui, sans-serif',
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: antdConfigToken,
        components: {
          Modal: {
            contentBg: isDark ? '#181818' : '#ffffff',
            headerBg: isDark ? '#181818' : '#ffffff',
            footerBg: isDark ? '#181818' : '#ffffff',
            titleColor: isDark ? '#fafbff' : '#060a26',
            colorIcon: isDark ? '#9496a0' : '#646a8c',
          },
          Button: {
            colorPrimary: isDark ? '#2ad99f' : '#23b383',
            colorPrimaryHover: isDark ? '#26b888' : '#1fa577',
            defaultBorderColor: isDark ? '#393a3f' : '#dcdee6',
            defaultColor: isDark ? '#fafbff' : '#060a26',
            defaultBg: isDark ? '#181818' : '#ffffff',
            defaultHoverBorderColor: isDark ? '#2ad99f' : '#23b383',
            defaultHoverColor: isDark ? '#2ad99f' : '#23b383',
            dangerColor: '#ffffff',
            colorErrorHover: '#d4563a',
          },
          Skeleton: {
            gradientFromColor: isDark ? '#242424' : '#f0f0f0',
            gradientToColor: isDark ? '#2e2e2e' : '#e0e0e0',
          },
          Select: {
            colorText: isDark ? '#fafbff' : '#060a26',
            colorTextPlaceholder: isDark ? '#9496a0' : '#646a8c',
            colorBgContainer: isDark ? '#0a0a0a' : '#ffffff',
            colorBorder: 'transparent',
            colorPrimaryHover: 'transparent',
            optionSelectedColor: isDark ? '#fafbff' : '#060a26',
            optionActiveBg: isDark ? 'rgba(250,251,255,0.08)' : 'rgba(6,10,38,0.06)',
            optionSelectedBg: isDark ? 'rgba(42,217,159,0.15)' : 'rgba(35,179,131,0.12)',
          },
          DatePicker: {
            activeBorderColor: isDark ? '#2ad99f' : '#23b383',
            hoverBorderColor: isDark ? '#25bf8c' : '#1e9c72',
            activeShadow: isDark
              ? '0 0 0 2px rgba(42,217,159,0.15)'
              : '0 0 0 2px rgba(35,179,131,0.12)',
            cellHoverBg: isDark ? 'rgba(42,217,159,0.12)' : 'rgba(35,179,131,0.08)',
            cellActiveWithRangeBg: isDark ? 'rgba(42,217,159,0.18)' : 'rgba(35,179,131,0.12)',
            cellHoverWithRangeBg: isDark ? 'rgba(42,217,159,0.24)' : 'rgba(35,179,131,0.16)',
            cellRangeBorderColor: isDark ? '#2ad99f' : '#23b383',
          },
        },
      }}
    >
      <AntdApp>
        <AuthProvider>
          <ModelCapabilityProvider>
            <UploadCenterProvider>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <LandingEntryRoute>
                        <LandingPage />
                      </LandingEntryRoute>
                    }
                  />
                  <Route path="/login" element={<LoginPage />} />
                  <Route
                    path="/canvas"
                    element={
                      <ProtectedRoute>
                        <UserProvider>
                          <Layout theme={theme} onThemeToggle={handleThemeToggle} fullscreen>
                            <CanvasRouteGuard theme={theme} onThemeToggle={handleThemeToggle} />
                          </Layout>
                        </UserProvider>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/guide" element={<UserGuidePage />} />
                  <Route path="/agent-access/resolve" element={<AgentAccessResolvePage />} />
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <UserProvider>
                          <Layout theme={theme} onThemeToggle={handleThemeToggle}>
                            <Suspense fallback={<LoadingFallback />}>
                              <Routes>
                                <Route path="projects" element={<ProjectDashboard />} />
                                <Route path="skills" element={<SkillManagement />} />
                                <Route path="team" element={<TeamWorkspacePage />}>
                                  <Route index element={<TeamPage />} />
                                  <Route
                                    path="projects/:projectKey/knowledge"
                                    element={<TeamKnowledgePage />}
                                  />
                                </Route>
                              </Routes>
                            </Suspense>
                          </Layout>
                        </UserProvider>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </UploadCenterProvider>
          </ModelCapabilityProvider>
        </AuthProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
